import { useState, useEffect } from 'react'
import _ from 'lodash'
import { supabaseGetter } from '~/api/supabase-getter'
import { useOrgContext } from '~/common/context/organization/org-context'
import { getDescendantOrgs } from '~/common/use-descendant-orgs'
import { useCropsContext } from '~/common/context/crops/crops-context'
import { useOrgParams } from '~/common/use-org-params'

/**
 * Fetch tasks for the active organization (hierarchical)
 *
 * Includes:
 * - Organization-level tasks (plot/patch maintenance)
 * - Crop-level tasks (planting, harvesting, etc.)
 * - Tasks from all descendant organizations
 *
 * Examples:
 * - Farm view: Tasks from farm + all plots + all patches + all crops
 * - Plot view: Tasks from plot + all patches + all crops
 * - Patch view: Tasks from patch + all crops
 *
 * @returns {Object} { tasks, loading, error, invalidate }
 */
export const useTasksInOrg = () => {
  const { activeOrgs } = useOrgContext()
  const { refreshKey } = useCropsContext()
  const { seasonId } = useOrgParams()
  const latestActiveOrg = _.last(activeOrgs)

  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    async function fetchTasks() {
      if (!latestActiveOrg) {
        setTasks([])
        setLoading(false)
        setError(null)
        return
      }

      setLoading(true)
      setError(null)
      setTasks([])

      try {
        // Get descendant organization IDs based on org type
        const descendantOrgIds = await getDescendantOrgs(
          latestActiveOrg.id,
          latestActiveOrg.organization_type
        )

        const allOrgIds = [latestActiveOrg.id, ...descendantOrgIds]

        // Fetch tasks for all organizations (optionally filtered by season)
        const { data, error: fetchError } = seasonId
          ? await supabaseGetter.tasks.getByOrganizationsAndSeason(allOrgIds, seasonId)
          : await supabaseGetter.tasks.getByOrganizations(allOrgIds)

        if (fetchError) throw fetchError

        setTasks(data || [])
      } catch (err) {
        console.error('Error fetching tasks:', err)
        setError(err.message)
        setTasks([])
      } finally {
        setLoading(false)
      }
    }

    fetchTasks()
  }, [latestActiveOrg, refreshKey, seasonId])

  // Update a specific task in the local state (optimistic update)
  const updateTaskInState = (taskId, updates) => {
    setTasks(prevTasks =>
      prevTasks.map(task =>
        task.id === taskId
          ? { ...task, ...updates }
          : task
      )
    )
  }

  return {
    tasks,
    loading,
    error,
    updateTaskInState,
    invalidate: supabaseGetter.tasks.invalidate
  }
}
